
import Component from 'vue-class-component';
import Vue from 'vue';
import { Prop, Watch } from 'vue-property-decorator';
import { ModalState } from '@/core/types/modal-state';
import { ProfileAPIService } from '@/profile/services/api';
import { TenDigitPhoneValidation, CodeValidation } from '@kasasa/kloans-common-ui/dist/validations';
import { Alert } from '@/core/types/alert';
import { AlertTypeEnum } from '@/core/types/alert-type-enum';
import { required } from 'vuelidate/lib/validators';
import { namespace } from 'vuex-class';
import { CoreHelper } from '@/core/helper';

const coreNamespace = namespace('core');

const INVALID_CODE_MESSAGE = 'Invalid code provided, please request a code again.';
const SUCCESS_MESSAGE = 'Your phone number has been successfully updated.';

@Component({
	validations: {
		phone: TenDigitPhoneValidation.validations(),
		code: {
			required,
			...CodeValidation.validations,
		},
	},
})
export default class ProfileMyProfileEditPhoneModal extends Vue {
	public alert: Alert | null = null;

	public code = '';

	public isConfirmLoading = false;

	public isShown = false;

	public isVerificationCodeSendLoading = false;

	public phone = '';

	public phoneUnmasked = '';

	@coreNamespace.Action('getAndSetSession')
	public getAndSetSession!: () => Promise<void>;

	@Prop({ default: { isShown: false } })
	public state!: ModalState;

	@Watch('state')
	public watchState(state: { isShown: boolean }): void {
		this.isShown = state.isShown;
	}

	public wasConfirmSuccessful = false;

	public wasVerificationCodeSent = false;

	get phoneErrors(): string[] {
		return TenDigitPhoneValidation.errors(this, 'phone');
	}

	get codeErrors(): string[] {
		if (this.code.length < 6) {
			return CodeValidation.errors(this, 'code', 6, 'Verification code');
		}

		return [];
	}

	public close(): void {
		this.isShown = false;
		this.reset();
	}

	public onPhoneInput($event: any): void {
		this.phoneUnmasked = $event.target.unmaskedValue;
		this.$v.phone.$touch();
	}

	public async sendVerificationCode(): Promise<void> {
		this.$v.phone.$touch();

		if (this.$v.phone.$invalid) {
			return;
		}

		this.isVerificationCodeSendLoading = true;
		this.wasVerificationCodeSent = false;

		try {
			await ProfileAPIService.updatePhone(this.phoneUnmasked);
			await this.getAndSetSession();

			this.wasVerificationCodeSent = true;
		} catch (error) {
			this.alert = {
				type: AlertTypeEnum.ERROR,
				message: CoreHelper.getHttpErrorMessage(error),
			};
		}

		this.isVerificationCodeSendLoading = false;
	}

	public async confirm() {
		this.$v.code.$touch();

		if (this.$v.code.$invalid) {
			return;
		}

		this.isConfirmLoading = true;

		try {
			const isCodeValid = await ProfileAPIService.verifyPhoneCode(this.code);

			if (isCodeValid) {
				await this.getAndSetSession();

				this.alert = {
					type: AlertTypeEnum.SUCCESS,
					message: SUCCESS_MESSAGE,
				};
			} else {
				this.alert = {
					type: AlertTypeEnum.ERROR,
					message: INVALID_CODE_MESSAGE,
				};
			}
		} catch (error) {
			console.error(error);

			this.alert = {
				type: AlertTypeEnum.ERROR,
				message: CoreHelper.getHttpErrorMessage(error),
			};
		}

		this.isConfirmLoading = false;
	}

	private reset(): void {
		this.alert = null;
		this.code = '';
		this.isConfirmLoading = false;
		this.isVerificationCodeSendLoading = false;
		this.phone = '';
		this.phoneUnmasked = '';
		this.wasConfirmSuccessful = false;
		this.wasVerificationCodeSent = false;
		this.$v.$reset();
	}
}
